/**
 * /admin/operations-manual — internal staff manual.
 *
 * Role-filtered, search-indexed, deep-link friendly. Audience filtering
 * happens at the group level (each group has an `audience` array; admins
 * see every group, clinicians get clinical + general, etc).
 *
 * Iter 130 — Sections + groups are now rendered inside shadcn Accordions.
 * Search auto-expands matched groups & sections; direct-link hash anchors
 * also open the target section on load. Cuts the visual page length by
 * ~80% and makes the manual navigable at a glance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BookOpen, Search } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { MANUAL } from './content';
import { renderBody, SetupVideo } from './helpers';
import { TrainingProgress } from './TrainingProgress';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../../components/ui/accordion';

export function OperationsManual() {
  const { user } = useAuth();
  const role = user?.role || 'admin';
  const [q, setQ] = useState('');
  const location = useLocation();

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return MANUAL
      .filter(g => g.audience.includes(role))
      .map(g => ({
        ...g,
        sections: g.sections.filter(s =>
          !needle
          || s.title.toLowerCase().includes(needle)
          || s.body.toLowerCase().includes(needle)
        ),
      }))
      .filter(g => g.sections.length > 0);
  }, [q, role]);

  // Which groups are open in the accordion. When the user searches, we
  // auto-expand every group that has a match so nothing is hidden.
  const [openGroups, setOpenGroups] = useState([]);
  useEffect(() => {
    if (q.trim()) {
      setOpenGroups(filtered.map(g => g.id));
    }
  }, [q, filtered]);

  // Track which section id is deep-linked via #hash — we auto-open both
  // its group AND the section itself, then scroll to it.
  const [hashSectionId, setHashSectionId] = useState('');
  useEffect(() => {
    const hash = location.hash.slice(1);
    if (!hash) return;
    const owningGroup = MANUAL.find(g => g.sections.some(s => s.id === hash));
    if (owningGroup) {
      setOpenGroups(prev => (prev.includes(owningGroup.id) ? prev : [...prev, owningGroup.id]));
      setHashSectionId(hash);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  }, [location.hash]);

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">REFERENCE</div>
        <h1 className="text-4xl font-heading font-bold tracking-tight inline-flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-brand"/> Daily operations manual
        </h1>
        <p className="text-fg-muted mt-2">Step-by-step playbooks for daily clinic operations. Filtered to your role: <strong className="capitalize">{role}</strong>.</p>
      </div>

      <TrainingProgress/>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"/>
          <input type="search" value={q} onChange={e=>setQ(e.target.value)}
                 placeholder="Search the manual…" className="input pl-9 w-full" data-testid="manual-search"/>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setOpenGroups(filtered.map(g => g.id))}
            className="btn-secondary text-xs"
            data-testid="manual-expand-all"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setOpenGroups([])}
            className="btn-secondary text-xs"
            data-testid="manual-collapse-all"
          >
            Collapse all
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-fg-muted">No sections match your search.</p>
      ) : (
        <Accordion
          type="multiple"
          value={openGroups}
          onValueChange={setOpenGroups}
          className="w-full border-t border-soft"
          data-testid="manual-accordion"
        >
          {filtered.map(g => (
            <AccordionItem key={g.id} value={g.id} className="border-soft" data-testid={`manual-group-${g.id}`}>
              <AccordionTrigger className="text-left" data-testid={`manual-group-trigger-${g.id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-heading font-semibold text-fg">{g.title}</span>
                  <span className="text-[11px] uppercase tracking-wider text-fg-muted">
                    {g.sections.length} {g.sections.length === 1 ? 'section' : 'sections'}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent id={g.id}>
                <SectionAccordion sections={g.sections} groupId={g.id} defaultOpenId={hashSectionId}/>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}

/**
 * Nested accordion that renders each section as its own collapsible.
 * Sections default closed; the one whose id matches the URL hash starts open.
 */
function SectionAccordion({ sections, groupId, defaultOpenId }) {
  const [open, setOpen] = useState(defaultOpenId ? [defaultOpenId] : []);
  useEffect(() => {
    if (defaultOpenId && sections.some(s => s.id === defaultOpenId)) {
      setOpen(prev => (prev.includes(defaultOpenId) ? prev : [...prev, defaultOpenId]));
    }
  }, [defaultOpenId, sections]);
  return (
    <Accordion
      type="multiple"
      value={open}
      onValueChange={setOpen}
      className="pl-2 border-l-2 border-[#F2EBDB]"
      data-testid={`manual-sections-${groupId}`}
    >
      {sections.map(s => (
        <AccordionItem key={s.id} value={s.id} className="border-soft/60">
          <AccordionTrigger
            className="text-left text-sm font-medium py-3"
            id={s.id}
            data-testid={`manual-section-trigger-${s.id}`}
          >
            {s.title}
          </AccordionTrigger>
          <AccordionContent>
            <div
              className="text-sm text-fg-secondary whitespace-pre-wrap leading-relaxed prose-sm"
              dangerouslySetInnerHTML={{ __html: renderBody(s.body) }}
            />
            <SetupVideo videoKey={s.id}/>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
